'use client';
import { DevelocityLogo } from '@/components/Logos';
import { useGSAP } from '@gsap/react';
import { ReactNode, useRef, useState } from 'react';
import gsap from 'gsap';
import clsx from 'clsx';
import { PlayCircleIcon } from '@heroicons/react/20/solid';
import { ModalVideoPlayer } from '@/components/ModalVideoPlayer';
import Button from '@/components/Button';
import dynamic from 'next/dynamic';
import type { LottieRefCurrentProps } from 'lottie-react';
import './hero.scss';

const Lottie = dynamic(() => import('lottie-react'), {
  ssr: false,
});

type HeroSectionProps = {
  h1: ReactNode;
  p: ReactNode;
  bg1: `[background:linear-gradient(${string})]`;
  bg2: `[background:linear-gradient(${string})]`;
  h1GradientStopColor: `to-[${string}]`;
  ellipseGradientColor: string;
  cta?: string;
  videoLink?: string;
  rightAnimation?: object;
  leftAnimation?: object;
};

export default function Hero({
  h1,
  p,
  bg1,
  bg2,
  h1GradientStopColor,
  ellipseGradientColor,
  cta,
  videoLink,
  rightAnimation,
  leftAnimation,
}: HeroSectionProps) {
  const containerRef = useRef<HTMLElement>(null);
  const rightAnimationRef = useRef<LottieRefCurrentProps>(null);
  const leftAnimationRef = useRef<LottieRefCurrentProps>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const reformatStartBG1 = bg1.slice(12, -1).replaceAll('_', ' ');
  const reformatStartBG2 = bg2.slice(12, -1).replaceAll('_', ' ');

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  useGSAP(
    () => {
      const tl = gsap.timeline({});
      // timeScale lets you easily adjust overall duration of animation 1.1 is sped up by 10%
      tl.timeScale(1.5);
      tl.to(
        '.anim-icon',
        {
          y: 0,
          delay: 0.5,
        },
        'start',
      )
        .fromTo(
          containerRef.current,
          {
            background: reformatStartBG1,
          },
          {
            background: reformatStartBG2,
          },
          'start+=0.55',
        )
        .to(
          ['.h1-anim', '.text-anim'],
          {
            y: 0,
            stagger: 0.1,
            autoAlpha: 1,
          },
          'start+=0.7',
        );

      if (cta) {
        tl.to(
          '.cta-anim',
          {
            y: 0,
            autoAlpha: 1,
          },
          'start+=0.75',
        );
      }

      tl.fromTo(
        '.img-anim',
        {
          y: 100,
          autoAlpha: 0,
        },
        {
          y: 0,
          autoAlpha: 1,
        },
        'start+=0.75',
      )
        // Add delay before starting the Lottie animation
        .to({}, { duration: 0.25 })
        .call(() => {
          rightAnimationRef.current?.play();
          leftAnimationRef.current?.play();
        });
    },
    { scope: containerRef },
  );

  return (
    <section
      id="hero"
      ref={containerRef}
      className={clsx(
        'max-h-screen-with-develocity-pages-sub-nav relative flex max-h-[calc(1000px_-_var(--navbarHeight))] !scroll-mt-[var(--develocityPagesSubNavHeight)] items-center justify-center overflow-hidden text-white',
        bg1,
      )}
    >
      <div
        className={clsx(
          'absolute z-10 flex w-full max-w-[47rem] flex-col items-center justify-center gap-4 px-4',
          cta ? 'top-[15%] md:top-[18%]' : 'top-1/4',
        )}
      >
        <DevelocityLogo monochrome className="anim-icon mb-4 w-40 translate-y-20" />
        <h1
          className={clsx(
            'h1-anim text-h1 translate-y-20 bg-gradient-to-b from-white bg-clip-text text-center text-transparent opacity-0',
            h1GradientStopColor,
          )}
        >
          {h1}
        </h1>
        <p className="text-anim text-eyebrow w-full translate-y-20 text-center text-gray-5 opacity-0 md:w-[27rem] lg:w-[36rem] xl:w-[45rem]">
          {p}
        </p>
        {cta && (
          <div className="cta-anim group flex translate-y-20 flex-col items-center justify-center gap-1 opacity-0">
            <PlayCircleIcon
              onClick={openModal}
              className="size-[4.5rem] cursor-pointer opacity-30 group-hover:opacity-100"
            />
            <Button
              onClick={openModal}
              variant="ghost-light"
              className="group-hover:bg-gradient-to-br group-hover:from-gradle-blue group-hover:to-gradle-green"
            >
              {cta}
            </Button>
          </div>
        )}
        <ModalVideoPlayer isOpen={isOpen} videoLink={videoLink} onClose={closeModal} />
      </div>
      {rightAnimation && (
        <Lottie
          lottieRef={rightAnimationRef}
          animationData={rightAnimation}
          autoplay={false}
          loop={false}
          className="absolute bottom-1/3 right-1/12 z-10 hidden w-1/5 lg:block"
        />
      )}
      {leftAnimation && (
        <Lottie
          lottieRef={leftAnimationRef}
          animationData={leftAnimation}
          autoplay={false}
          loop={false}
          className="absolute bottom-1/2 left-1/12 z-10 hidden w-1/5 lg:block"
        />
      )}

      <div
        className="ellipse img-anim bg-gray-1"
        style={{ '--gradient-color': `${ellipseGradientColor}` } as React.CSSProperties}
      ></div>
    </section>
  );
}
